/* eslint-disable react-hooks/exhaustive-deps */
import { memo, useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { withTranslation, Trans } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import { stringify } from 'query-string';
import Box from '@mui/material/Box';
import MenuExpComp from '@experimental-components/Menu';

import Layout, { ContentLayout } from 'Layout';

import { withKitchenAreas } from 'providers/settings/KitchenAreas';
import { withRequiredLicense } from 'providers/root/AuthProvider';
import { withProducts } from 'providers/menuMaker/Products';
import { withCategories } from 'providers/menuMaker/Categories';
import { withProductDetails } from 'providers/menuMaker/ProductDetails';
import { withMultiTaxes } from 'providers/settings/MultiTaxes';
import { useGetCategoryCatalog } from 'providers/menuMaker/Categories/hooks';
import { useGetModifierGroupsCatalog } from 'providers/menuMaker/ModifierGroups/hooks';
import { useSidebarLayout } from 'providers/root/SidebarLayoutProvider';
import useAmplitude from 'providers/root/AmplitudeProvider/useAmplitude';

import useNotification from '@design-system/Notification/useNotification';
import PageError from 'components/PageError';
import useUserStore from 'hooks/useUserStore';
import MenuMakerTabsNavigation from 'components/MenuMakerTabsNavigation';
import HeaderProductsFilters from 'components/HeaderProductsFilters';
import ProductDrawer from 'components/ProductDrawer';

import useSortingProducts from 'hooks/useSortingProducts';

import { statusType } from 'utils/products';
import { getEnabledKitchenAreas } from 'utils/kitchenAreas/makeKitchenAreasFields';

import {
  drawerActions,
  drawerActionList,
  drawerEntities,
  drawerEntityList,
  getQueryParamsMenuMaker,
} from 'utils/drawerMenuMaker';

import { getErrorCode, errorsCodeTypes } from 'utils/parrotErrorCode';

import { ListItemText, Slide, useTheme } from '@mui/material';

import AddOutline from '@experimental-components/IconsComponents/AddOutline';

import AcionableFloatingButton from '@experimental-components/AcionableFloatingButton';

import Menu from 'components/Inventories/Supplies/Menu';

import useTypesOfProducts from 'containers/SettingsTypeOfProducts/hooks/useProductTypes';

import TitleSection from 'components/TitleSection';
import { useCommandBarMenus } from 'hooks/useCommandBarAttributes';
import { useMenuScopeIdentifier } from 'hooks/menuScope';
import { getScopeBasedTranslationKeys } from 'utils/menuMaker/scopeTranslations';

import { MenuMakerImport } from 'components/MenuMakeImport';

import { useImportMenuModal } from 'components/MenuMakeImport/hooks';

import ProductsTableWrapper from './ProductsTableWrapper';
import useStyles from './styles';
import TypeOfProductModal from './components/TypeOfProductModal';
import useAssignTypeOfProducts from './hooks/useAssignTypeOfProduct';

export function Products({
  loadProducts,
  products,
  categories,
  productsFetching,
  productsFetched,
  productsError,
  resetProducts,
  createProduct,
  createProductObject,
  createProductFetching,
  createProductFetched,
  createProductError,
  resetCreateProduct,
  handleToggleProductAvailability,
  toggleProductAvailabilityFetching,
  toggleProductAvailabilityFetched,
  toggleProductAvailabilityError,
  resetToggleProductAvailability,
  resetDeleteProduct,
  updateProductImage,
  updateProductImageFetching,
  updateProductImageFetched,
  updateProductImageError,
  resetUpdateProductImage,
  loadProductsScrollPagination,
  productsPaginationInfo,
  loadProductsPaginationScrollState,
  resetProductsScrollPagination,
  loadKitchenAreas,
  kitchenAreas,
  kitchenAreasState,
  resetKitchenAreas,
  loadGetProduct,
  productDetailsObject,
  productDetailsState,
  resetProductDetails,
  editProduct,
  productEditObject,
  productEditState,
  resetEditProduct,
  duplicateProduct,
  duplicateProductState,
  resetDuplicateProduct,
  loadMultiTaxes,
  multiTaxesWithNoOption,
  multiTaxesState,
  resetMultiTaxes,
  t,
}) {
  const { importMenuModalIsOpen } = useImportMenuModal();
  const { modeDraft, isPublishMenuError } = useSidebarLayout();
  const history = useHistory();
  const location = useLocation();
  const theme = useTheme();
  const commandBarHook = useCommandBarMenus();
  const [typeOfProductAvailability, setTypeOfProductAvailability] = useState(null);

  // Menu scope context for functionality control
  const { isStoreMode, isOrganizationScope } = useMenuScopeIdentifier();

  // Get scope-based translation keys
  const { titleKey, descriptionKey } = getScopeBasedTranslationKeys(isOrganizationScope, 'articles');
  const [openDrawer, setOpenDrawer] = useState(false);
  const [selectedProductUuid, setSelectedProductUuid] = useState(null);
  const classes = useStyles({ openDrawer, isModeDraft: modeDraft || isPublishMenuError });
  const [elMenuAction, setMenuAction] = useState(null);
  const [isCheckAll, setIsCheckAll] = useState(false);
  const [isIndeterminate, setIsIndeterminate] = useState(false);
  const [productSelectedRow, setProductSelectedRow] = useState([]);
  const [excludedProductsOrdersLocal, setExcludedProductsOrdersLocal] = useState([]);
  const [typeOfProduct, setTypeOfProduct] = useState('');
  const [openModal, setOpenModal] = useState(false);
  const {
    storeId,
    storeState: { selected: storeSelected },
  } = useUserStore();
  const { setSortingByFilters, filtersSorting, resetSorting, cacheFilters } = useSortingProducts();
  const { closeAllSnackbar, setNotification } = useNotification();
  const { loadCategoryCatalog, categories: categoriesList, categoriesState, resetCategories } = useGetCategoryCatalog(
    storeId,
  );
  const { modifierGroups, modifierGroupsState, loadModifierGroups, resetModifierGroups } = useGetModifierGroupsCatalog(
    storeId,
  );
  const { logEvent } = useAmplitude();
  const { typesOfProducts, typesOfProductsState } = useTypesOfProducts({ enabled: true });
  const assignTypeOfProductsMutation = useAssignTypeOfProducts();

  const { action, entity, uuid } = getQueryParamsMenuMaker(location);
  const isEdition = action === drawerActions.EDIT;

  useEffect(() => {
    logEvent('@event.$menuMaker.viewMenuMaker', '@@section.products');
    setTimeout(() => {
      loadProducts({ page: 1 });
    }, 0);
  }, []);

  useEffect(() => {
    const isValid = drawerActionList.includes(action) && drawerEntityList.includes(entity);

    setOpenDrawer(Boolean(isEdition ? isValid && uuid : isValid));
  }, [action, entity, uuid, isEdition]);

  useEffect(() => {
    if (createProductFetched) {
      logEvent('@event.$menuMaker.menuMaker', ['@@section.products', '@@type.new', '@@item.products'], {
        itemId: createProductObject?.uuid,
        store: storeSelected?.uuid,
        isDraft: storeSelected?.isDraft,
      });
      loadProducts({ ...filtersSorting, isSorting: true, page: 1 });
      setNotification(
        t('menuMaker:productForm.messages.saveSuccess', {
          productName: createProductObject?.name,
        }),
        { variant: 'success' },
      );
      history.replace({ search: '' });
      resetCreateProduct();
    }

    if (createProductError && !getErrorCode(createProductError, errorsCodeTypes.PRODUCT_BARCODE_DUPLICATED)) {
      setNotification(t('menuMaker:productForm.messages.saveError'), { variant: 'error' });
      resetCreateProduct();
    }
  }, [createProductFetched, createProductError, history, setNotification, t]);

  useEffect(() => {
    if (updateProductImageFetched) {
      setNotification(t('menuMaker:productUpdateImage.updateImageSuccess'), { variant: 'success' });
    }

    if (updateProductImageError) {
      setNotification(t('menuMaker:productUpdateImage.updateImageError'), { variant: 'error' });
    }
  }, [setNotification, t, updateProductImageError, updateProductImageFetched]);

  useEffect(() => {
    if (toggleProductAvailabilityFetched && typeOfProductAvailability === statusType.STATUS_UNAVAILABLE) {
      setNotification(t('menuMaker:messages.inStockHidedSuccess'), { variant: 'success' });
    } else if (toggleProductAvailabilityFetched && typeOfProductAvailability === statusType.STATUS_AVAILABLE) {
      setNotification(t('menuMaker:messages.inStockIncludedSuccess'), { variant: 'success' });
    }

    if (toggleProductAvailabilityFetched || toggleProductAvailabilityError) {
      resetToggleProductAvailability();
    }
  }, [
    toggleProductAvailabilityFetched,
    toggleProductAvailabilityError,
    resetToggleProductAvailability,
    typeOfProductAvailability,
    setNotification,
    t,
  ]);

  useEffect(() => {
    if (productEditState.fetched) {
      logEvent('@event.$menuMaker.menuMaker', ['@@section.products', '@@type.edit', '@@item.products'], {
        itemId: productEditObject?.uuid,
        store: storeSelected?.uuid,
        isDraft: storeSelected?.isDraft,
      });
      loadProducts({ ...filtersSorting, isSorting: true, page: 1 });
      setNotification(
        t('menuMaker:productForm.messages.editSuccess', {
          productName: productEditObject?.name,
        }),
        { variant: 'success' },
      );
      history.replace({ search: '' });
      resetEditProduct();
    }

    if (productEditState.error && !getErrorCode(productEditState.error, errorsCodeTypes.PRODUCT_BARCODE_DUPLICATED)) {
      setNotification(t('menuMaker:productForm.messages.editError'), { variant: 'error' });
      resetEditProduct();
    }
  }, [productEditState, setNotification, t]);

  useEffect(() => {
    if (duplicateProductState.fetched) {
      logEvent('@event.$menuMaker.menuMaker', ['@@section.products', '@@type.duplicate', '@@item.products'], {
        itemId: selectedProductUuid,
        store: storeSelected?.uuid,
        isDraft: storeSelected?.isDraft,
      });
      loadProducts({ ...filtersSorting, isSorting: true, page: 1 });
      setNotification(t('menuMaker:productForm.messages.duplicateSuccess'), { variant: 'success' });
      setSelectedProductUuid(null);
      resetDuplicateProduct();
    }
    if (duplicateProductState.error) {
      setNotification(t('menuMaker:productForm.messages.duplicateError'), { variant: 'error' });
      resetDuplicateProduct();
    }
  }, [duplicateProductState]);

  useEffect(() => {
    return function cleanup() {
      resetProducts();
      resetCreateProduct();
      resetDeleteProduct();
      resetToggleProductAvailability();
      resetUpdateProductImage();
      resetEditProduct();
      resetDuplicateProduct();
      resetSorting();
      closeAllSnackbar();
    };
  }, [
    resetProducts,
    resetCreateProduct,
    resetDeleteProduct,
    resetToggleProductAvailability,
    resetUpdateProductImage,
    resetSorting,
    closeAllSnackbar,
  ]);

  useEffect(() => {
    if (assignTypeOfProductsMutation?.isSuccess && !assignTypeOfProductsMutation?.isLoading) {
      setNotification(t('menuMaker:typeOfProduct.modal.assignTypeOfProductSuccess'), { variant: 'success' });
    }
    if (assignTypeOfProductsMutation?.isError && !assignTypeOfProductsMutation?.isLoading) {
      setNotification(t('menuMaker:typeOfProduct.modal.assignTypeOfProductError'), { variant: 'error' });
    }
  }, [assignTypeOfProductsMutation?.isSuccess, assignTypeOfProductsMutation?.isError]);

  function handleSortingByTextProducts(values) {
    const { filterByText } = values;

    if (filterByText?.length >= 3) {
      logEvent('@event.$menuMaker.menuMakerFilters', '@@section.products', { search: filterByText });
      setSortingByFilters({ ...values });
      setIsCheckAll(false);
    }

    if (!filterByText) {
      setSortingByFilters({ ...values, filterByText: null });
      setIsCheckAll(false);
    }
  }

  useEffect(() => {
    setProductSelectedRow([]);
    setExcludedProductsOrdersLocal([]);
  }, [cacheFilters]);

  function handleSortingByCategories(values) {
    setSortingByFilters({ ...values });
    setIsCheckAll(false);
  }

  function handelSortingByTypeSold(values) {
    setSortingByFilters({ ...values });
    setIsCheckAll(false);
  }

  function handleSortingByBrands(values) {
    setSortingByFilters({ ...values });
    setIsCheckAll(false);
  }

  function handleSortingOrderBy(values) {
    setSortingByFilters({ ...values });
    setIsCheckAll(false);
  }

  function handleOpenNewProduct() {
    const query = { action: drawerActions.CREATE, entity: drawerEntities.PRODUCT };

    history.replace({ search: stringify(query) });
  }

  function handleLoadProducts() {
    resetProductsScrollPagination();
    loadProducts({ ...filtersSorting, isSorting: true, page: 1 });
  }
  const handlePaginationProducts = useCallback(() => {
    const currentPage = productsPaginationInfo.page + 1;

    if (!loadProductsPaginationScrollState.fetching && currentPage !== productsPaginationInfo?.page) {
      loadProductsScrollPagination({ ...filtersSorting, page: currentPage });
    }
  }, [
    loadProductsPaginationScrollState.fetching,
    loadProductsScrollPagination,
    productsPaginationInfo.page,
    filtersSorting,
  ]);

  function hanldeUpdateProductImage(productUuid, image) {
    updateProductImage({ storeUuid: storeId, productUuid, image });
  }

  function handleToggleProductStock(productParams) {
    setTypeOfProductAvailability(productParams.availability);
    handleToggleProductAvailability(productParams);
  }

  function handleDuplicateProduct(productUuid) {
    setSelectedProductUuid(productUuid);
    duplicateProduct({ storeUuid: storeId, productUuid });
  }

  function handleLoadCategories() {
    loadCategoryCatalog();
  }

  function handleLoadModifierGroups() {
    loadModifierGroups();
  }

  const handleLoadProductDetails = () => {
    loadGetProduct(storeId, uuid);
  };

  const handleSubmitProduct = (data) => {
    if (isEdition) {
      editProduct({ storeUuid: storeId, product: { uuid, ...data } });
    } else {
      createProduct({ storeUuid: storeId, product: { ...data } });
    }
  };

  function onClickPrimaryyButton(event) {
    setMenuAction(event.currentTarget);
  }

  const handleCancelAssignProductType = () => {
    setExcludedProductsOrdersLocal([]);
    setProductSelectedRow([]);
    setIsCheckAll(false);
  };

  function handleTypeOfProduct(value) {
    setTypeOfProduct(value);
  }

  function handleOpenModalTypeOfProduct() {
    setOpenModal(true);
  }

  function handleCloseModalTypeOfProduct() {
    setOpenModal(false);
    setTypeOfProduct('');
  }

  function handleModalSubmit() {
    let payload = {};
    if (!isCheckAll || isIndeterminate) {
      payload = {
        products: productSelectedRow,
        productTypeUuid: typeOfProduct,
      };
    } else {
      payload = {
        productTypeUuid: typeOfProduct,
        products: [],
        name: cacheFilters.filterByText,
        category: cacheFilters.filterByCategories,
        type_of_sale: cacheFilters.typeOfSale,
        brand: cacheFilters.filterByBrands,
        without_brands: cacheFilters.withoutBrands,
        without_categories: cacheFilters.withoutCategories,
        order_by: cacheFilters.orderBy,
      };
    }
    assignTypeOfProductsMutation.mutate(payload);
    setOpenModal(false);
    setMenuAction(null);
    setTypeOfProduct('');
  }

  function handleIndeterminate(value) {
    setIsIndeterminate(value);
  }

  return (
    <Layout>
      <ContentLayout>
        <ContentLayout.Header className={classes.headerLayout} sticky>
          <MenuMakerTabsNavigation />
          {productSelectedRow.length > 0 && !openDrawer && (
            <Slide className={classes.floatingBar} direction="down" in mountOnEnter unmountOnExit>
              <Box className={classes.buttonReduce} id="irf87">
                <AcionableFloatingButton
                  cancelText={t('menuMaker:typeOfProduct.buttons.cancel')}
                  hasCancelButton
                  helperNumSelected={productSelectedRow.length || 0}
                  helperText={
                    (productSelectedRow.length || 0) > 1
                      ? t('menuMaker:typeOfProduct.buttons.productsSelected2')
                      : t('menuMaker:typeOfProduct.buttons.productsSelected1')
                  }
                  isCheckAll={isCheckAll && !isIndeterminate}
                  isLoading={productsFetching}
                  onCancel={handleCancelAssignProductType}
                  onClick={onClickPrimaryyButton}
                  startIcon={<AddOutline color={theme.palette.common.white} />}
                >
                  {t('menuMaker:typeOfProduct.buttons.actions')}
                </AcionableFloatingButton>
              </Box>
            </Slide>
          )}
        </ContentLayout.Header>

        <ContentLayout.Container pb="0" pl="0" pr="0">
          <Box className={classes.root} pt={4}>
            <TitleSection
              commandBarHook={commandBarHook}
              description={<Trans i18nKey={descriptionKey} />}
              title={t(titleKey)}
            />
            <HeaderProductsFilters
              categoriesList={categories}
              isOrganizationMode={!isStoreMode}
              onAddButtonClick={handleOpenNewProduct}
              onSearchBoxChange={handleSortingByTextProducts}
              onSelectBrandsChange={handleSortingByBrands}
              onSelectTypeSoldProductChange={handelSortingByTypeSold}
              onSelectedCategoriesChange={handleSortingByCategories}
              onSelectedOrderBy={handleSortingOrderBy}
              openDrawer={openDrawer}
              px={4}
            />

            {(productsError || loadProductsPaginationScrollState.error) && (
              <PageError
                data-testid="menusError"
                id="menusError"
                labelAction={t('common:buttons.retry')}
                message={t('common:errors.loaded.products')}
                onAction={handleLoadProducts}
              />
            )}

            {!productsError && !loadProductsPaginationScrollState.error && (
              <Box mb={6}>
                <ProductsTableWrapper
                  categoriesByUuid={categories}
                  excludedProductsOrdersLocal={excludedProductsOrdersLocal}
                  isCheckAll={isCheckAll}
                  isLoading={productsFetching || (!products.length && !productsFetched)}
                  isSearchFilter={filtersSorting?.isSearchFilter}
                  loadingEnabledProduct={toggleProductAvailabilityFetching}
                  loadingUpdateProductImage={updateProductImageFetching}
                  onClearNotification={closeAllSnackbar}
                  onDuplicateProduct={handleDuplicateProduct}
                  onPaginate={handlePaginationProducts}
                  onUpdateProductImage={hanldeUpdateProductImage}
                  openDrawer={openDrawer}
                  paginateState={loadProductsPaginationScrollState}
                  paginateValues={productsPaginationInfo}
                  productSelectedRow={productSelectedRow}
                  products={products}
                  setExcludedProductsOrdersLocal={setExcludedProductsOrdersLocal}
                  setIsCheckAll={setIsCheckAll}
                  setIsIndeterminate={handleIndeterminate}
                  setProductSelectedRow={setProductSelectedRow}
                  toggleProductStock={handleToggleProductStock}
                />
              </Box>
            )}
          </Box>

          <ProductDrawer
            actionState={
              isEdition
                ? { ...productEditState, resetState: resetEditProduct }
                : {
                    fetching: createProductFetching,
                    fetched: createProductFetched,
                    error: createProductError,
                    resetState: resetCreateProduct,
                  }
            }
            categoriesState={{
              list: categoriesList,
              fetched: categoriesState.fetched,
              fetching: categoriesState.fetching,
            }}
            isEdition={isEdition}
            isOrganizationMode={!isStoreMode}
            kitchenAreasState={{ list: getEnabledKitchenAreas(kitchenAreas), ...kitchenAreasState }}
            modifierGroupsState={{
              list: modifierGroups,
              fetched: modifierGroupsState.fetched,
              fetching: modifierGroupsState.fetching,
            }}
            multiTaxesState={{ list: multiTaxesWithNoOption, ...multiTaxesState }}
            onLoadCategories={handleLoadCategories}
            onLoadKitchenAreas={loadKitchenAreas}
            onLoadModifierGroups={handleLoadModifierGroups}
            onLoadMultiTaxes={loadMultiTaxes}
            onLoadProductDetails={handleLoadProductDetails}
            onResetCategories={resetCategories}
            onResetGetKitchenAreas={resetKitchenAreas}
            onResetModifierGroups={resetModifierGroups}
            onResetMultitaxes={resetMultiTaxes}
            onResetProductDetails={resetProductDetails}
            onSubmit={handleSubmitProduct}
            openDrawer={openDrawer}
            productState={{ data: productDetailsObject, ...productDetailsState }}
            uuidSelected={uuid}
          />
          {importMenuModalIsOpen && <MenuMakerImport onSuccess={handleLoadProducts} />}
        </ContentLayout.Container>
      </ContentLayout>
      <Menu
        anchorEl={elMenuAction}
        classes={{ paper: classes.paper }}
        customWidth="150px"
        onClose={() => setMenuAction(null)}
        open={Boolean(elMenuAction)}
      >
        <MenuExpComp.Item className={classes.menuItem} onClick={() => handleOpenModalTypeOfProduct()}>
          <ListItemText primary="Agregar tipo" />
        </MenuExpComp.Item>
      </Menu>
      <TypeOfProductModal
        handleTypeOfProduct={handleTypeOfProduct}
        isDisabled={typesOfProductsState.isFetching}
        isLoading={typesOfProductsState.isFetching}
        isOpen={openModal}
        modalTitle="Agregar tipo de artículo"
        onCancel={handleCloseModalTypeOfProduct}
        onSubmit={handleModalSubmit}
        productNumber={productSelectedRow.length}
        typeOfProduct={typeOfProduct}
        typesOfProducts={typesOfProducts}
      />
    </Layout>
  );
}

Products.propTypes = {
  loadProducts: PropTypes.func,
  products: PropTypes.array,
  categories: PropTypes.object,
  productsFetching: PropTypes.bool,
  productsFetched: PropTypes.bool,
  productsError: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.bool]),
  resetProducts: PropTypes.func,
  createProduct: PropTypes.func,
  createProductObject: PropTypes.object,
  createProductFetching: PropTypes.bool,
  createProductFetched: PropTypes.bool,
  resetCreateProduct: PropTypes.func,
  createProductError: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.bool]),
  handleToggleProductAvailability: PropTypes.func,
  toggleProductAvailabilityFetching: PropTypes.bool,
  toggleProductAvailabilityFetched: PropTypes.bool,
  toggleProductAvailabilityError: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.bool]),
  resetToggleProductAvailability: PropTypes.func,
  resetDeleteProduct: PropTypes.func,
  updateProductImage: PropTypes.func,
  updateProductImageFetching: PropTypes.bool,
  updateProductImageFetched: PropTypes.bool,
  updateProductImageError: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.bool]),
  resetUpdateProductImage: PropTypes.func,
  loadProductsScrollPagination: PropTypes.func,
  productsPaginationInfo: PropTypes.object,
  loadProductsPaginationScrollState: PropTypes.object,
  loadKitchenAreas: PropTypes.func,
  kitchenAreas: PropTypes.array,
  kitchenAreasState: PropTypes.object,
  resetKitchenAreas: PropTypes.func,
  loadGetProduct: PropTypes.func,
  productDetailsObject: PropTypes.object,
  productDetailsState: PropTypes.object,
  resetProductDetails: PropTypes.func,
  editProduct: PropTypes.func,
  productEditObject: PropTypes.object,
  productEditState: PropTypes.object,
  resetEditProduct: PropTypes.func,
  duplicateProduct: PropTypes.func,
  duplicateProductState: PropTypes.object,
  resetDuplicateProduct: PropTypes.func,
  loadMultiTaxes: PropTypes.func,
  multiTaxesWithNoOption: PropTypes.array,
  multiTaxesState: PropTypes.object,
  resetMultiTaxes: PropTypes.func,
  resetProductsScrollPagination: PropTypes.func,
  t: PropTypes.func,
};

export default compose(
  memo,
  withRequiredLicense(),
  withProductDetails,
  withCategories,
  withProducts,
  withKitchenAreas,
  withMultiTaxes,
  withTranslation(['menuMaker', 'kitchenAreas', 'settingsTaxes']),
)(Products);
